import React from "react";
import { useTranslation } from "react-i18next";
import { PaymentsRoutesResponse } from "src/api";
import { Button, ErrorAlert, Field, InfoField, PaymentLimits } from "src/components";
import { useTrustPaymentsDeposit } from "src/hooks";
import { formatNumberToLocale, Rounds } from "src/utils";
import TrustPaymentsCardForm from "./TrustPaymentsCardForm";
import "./trustpayments-deposit-form.scss";

// Two-step UI:
//   1. Amount input.
//   2. Hosted card form (SecureTrading SDK iframes) once the BE
//      returns a `session_id`.
// Source / target currencies come from the route's meta — no FX
// inputs, the cardholder's bank does the FX, Trust settles us in
// the route currency.
const TrustPaymentsDepositForm: React.FC<{
  route: PaymentsRoutesResponse;
}> = ({ route }): JSX.Element => {
  const { t } = useTranslation();
  const {
    sourceCurrency,
    targetCurrency,
    sourceCurrencyPrecision,
    targetCurrencyPrecision,
    amount,
    handleAmountChange,
    amountTouched,
    setAmountTouched,
    amountValid,
    sourceAmount,
    loading,
    error,
    sessionId,
    kycFieldsRequired,
    submit,
    submitDisabled,
    reset,
    platformFee,
    feeCurrency,
    quoteLoading,
    totalReceiveAmount,
    platformFeeExceedsReceive,
    secondsUntilRefresh,
    fxEnabled,
    reverse,
    fxQuote,
    fxError,
    localCurrency,
    limitsStatus,
    availableLimit,
    limitsLoading,
    limitsCurrency,
    limitExceeded,
    belowMinLimit,
    minLimitAmount,
  } = useTrustPaymentsDeposit(route);

  if (!targetCurrency || !sourceCurrency) {
    return (
      <section className="trustpayments-deposit-form trustpayments-deposit-form__unavailable">
        <p className="trustpayments-deposit-form__unavailable-text">
          {t("TRUSTPAYMENTS_DEPOSIT.UNAVAILABLE", {
            defaultValue: "Card payments are not configured for this exchange.",
          })}
        </p>
      </section>
    );
  }

  if (sessionId) {
    return (
      <TrustPaymentsCardForm
        sessionId={sessionId}
        kycFieldsRequired={kycFieldsRequired}
        onReset={reset}
        sourceAmount={sourceAmount}
        totalReceiveAmount={totalReceiveAmount}
        sourceCurrency={sourceCurrency}
        targetCurrency={targetCurrency}
        sourceCurrencyPrecision={sourceCurrencyPrecision}
        targetCurrencyPrecision={targetCurrencyPrecision}
        platformFee={platformFee}
        feeCurrency={feeCurrency || targetCurrency}
        fxQuote={fxQuote}
      />
    );
  }

  const amountError =
    amountTouched && !amountValid
      ? t("TRUSTPAYMENTS_DEPOSIT.AMOUNT_REQUIRED", {
          defaultValue: "Enter an amount greater than 0",
        })
      : undefined;
  const receiveAmountError = platformFeeExceedsReceive
    ? t("TRUSTPAYMENTS_DEPOSIT.RECEIVE_AMOUNT_TOO_LOW", {
        defaultValue: "Platform fee exceeds the receive amount. Enter a higher amount.",
      })
    : null;
  const limitError = limitExceeded
    ? t("VALIDATION.LIMIT_EXCEEDED", {
        paymentType: "deposit",
        available: formatNumberToLocale(availableLimit, 2),
        currency: limitsCurrency,
        defaultValue: `Amount exceeds your available deposit limit (${formatNumberToLocale(availableLimit, 2)} ${limitsCurrency})`,
      })
    : null;
  const minLimitError = belowMinLimit
    ? t("VALIDATION.BELOW_MIN_LIMIT", {
        paymentType: "deposit",
        minimum: formatNumberToLocale(minLimitAmount, 2),
        currency: limitsCurrency,
        defaultValue: `Amount is below the minimum deposit (${formatNumberToLocale(minLimitAmount, 2)} ${limitsCurrency})`,
      })
    : null;

  // Reverse: user enters how much to SPEND in their local currency and the
  // quote shows the tokens received. Forward: user enters the target crypto.
  const amountLabel = reverse
    ? localCurrency
      ? t("TRUSTPAYMENTS_DEPOSIT.SPEND_AMOUNT_IN", {
          currency: localCurrency,
          defaultValue: `How much you want to spend (${localCurrency})`,
        })
      : t("TRUSTPAYMENTS_DEPOSIT.SPEND_AMOUNT", {
          defaultValue: "How much you want to spend",
        })
    : t("TRUSTPAYMENTS_DEPOSIT.PAY_AMOUNT_IN", {
        currency: targetCurrency,
        defaultValue: `Amount in ${targetCurrency}`,
      });

  // Distinct, actionable copy per FX failure. Amount min/max are
  // user-fixable; provider/country are not "retry" situations either, but
  // we keep the original retry copy for the genuine provider outage.
  // Gated on `amountTouched` (set on blur/submit) so the amount-based errors
  // don't flash mid-typing — e.g. "too low" after the first digit on the way
  // to a valid amount.
  const fxErrorMessage =
    fxEnabled && fxError && amountTouched
      ? fxError === "FX_AMOUNT_BELOW_MIN"
        ? t("TRUSTPAYMENTS_DEPOSIT.AMOUNT_BELOW_MIN", {
            defaultValue: "Amount is too low. Please enter a higher amount.",
          })
        : fxError === "FX_AMOUNT_ABOVE_MAX"
          ? t("TRUSTPAYMENTS_DEPOSIT.AMOUNT_ABOVE_MAX", {
              defaultValue: "Amount is too high. Please enter a lower amount.",
            })
          : fxError === "FX_COUNTRY_NOT_MAPPED"
            ? t("TRUSTPAYMENTS_DEPOSIT.COUNTRY_NOT_SUPPORTED", {
                defaultValue:
                  "Local-currency deposits aren't available for your country.",
              })
            : t("TRUSTPAYMENTS_DEPOSIT.FX_QUOTE_UNAVAILABLE", {
                defaultValue:
                  "Couldn't load your local rate. Please try again in a moment.",
              })
      : null;

  return (
    <form
      className="trustpayments-deposit-form"
      onSubmit={(e) => {
        e.preventDefault();
        setAmountTouched(true);
        submit();
      }}
    >
      <Field
        label={amountLabel}
        value={amount}
        onChange={(value) => handleAmountChange(value)}
        placeholder="0.00"
        disabled={loading}
        error={amountError}
        onBlur={() => setAmountTouched(true)}
      />

      <InfoField
        className="info-field--muted"
        label={t("TRUSTPAYMENTS_DEPOSIT.PLATFORM_FEE", {
          defaultValue: "Platform fee",
        })}
        value={`${formatNumberToLocale(platformFee, targetCurrencyPrecision, Rounds.HalfUp, true)} ${feeCurrency || targetCurrency}`}
        isLoading={quoteLoading}
      />

      <InfoField
        label={
          localCurrency
            ? t("TRUSTPAYMENTS_DEPOSIT.TOTAL_TO_PAY_LOCAL", {
                currency: localCurrency,
                defaultValue: `You'll pay in ${localCurrency}`,
              })
            : t("TRUSTPAYMENTS_DEPOSIT.TOTAL_TO_SPEND", {
                defaultValue: "Total to spend",
              })
        }
        value={
          fxQuote
            ? `${formatNumberToLocale(fxQuote.local_amount, 2)} ${fxQuote.local_currency}`
            : amountValid && sourceAmount > 0
              ? `${formatNumberToLocale(sourceAmount, sourceCurrencyPrecision)} ${sourceCurrency}`
              : "—"
        }
        isLoading={quoteLoading}
      />

      <InfoField
        label={t("TRUSTPAYMENTS_DEPOSIT.TOTAL_TO_RECEIVE", {
          defaultValue: "Total to receive",
        })}
        value={
          // Reverse has no client-side estimate: only show the crypto
          // received once the backend quote lands; never echo the local
          // spend input here.
          (reverse ? !!fxQuote : amountValid)
            ? `~${formatNumberToLocale(totalReceiveAmount, targetCurrencyPrecision, Rounds.HalfUp, true)} ${targetCurrency}`
            : "—"
        }
        isLoading={quoteLoading}
      />

      {amountValid && sourceAmount > 0 && (
        <p className="trustpayments-deposit-form__quote-refresh">
          {quoteLoading
            ? t("TRUSTPAYMENTS_DEPOSIT.QUOTE_REFRESHING", {
                defaultValue: "Refreshing price…",
              })
            : t("TRUSTPAYMENTS_DEPOSIT.QUOTE_REFRESHES_IN", {
                seconds: secondsUntilRefresh,
                defaultValue: `Price refreshes in ${secondsUntilRefresh}s`,
              })}
        </p>
      )}

      <PaymentLimits
        limitsStatus={limitsStatus}
        availableLimit={availableLimit}
        currency={limitsCurrency}
        isLoading={limitsLoading}
      />

      <p className="trustpayments-deposit-form__disclaimer">
        {fxQuote
          ? t("TRUSTPAYMENTS_DEPOSIT.FX_RATE_LOCKED_NOTE", {
              local_currency: fxQuote.local_currency,
              target_currency: targetCurrency,
              defaultValue: `Rate is locked for ${fxQuote.local_currency}, so the amount your bank charges is final. The ${targetCurrency} you receive is approximate — set at settlement by the live market rate. Your bank may also apply additional fees.`,
            })
          : t("TRUSTPAYMENTS_DEPOSIT.FINAL_RECEIVE_RATE_NOTE", {
              currency: targetCurrency,
              defaultValue: `Total to receive is approximate — the exact ${targetCurrency} is set at settlement by the live market rate.`,
            })}
      </p>

      <ErrorAlert message={error || receiveAmountError || fxErrorMessage || minLimitError || limitError} />

      <Button
        title={t("TRUSTPAYMENTS_DEPOSIT.CONTINUE", {
          defaultValue: "Continue to payment",
        })}
        className="deposit-button"
        type="submit"
        disabled={submitDisabled}
        pending={loading}
      />
    </form>
  );
};

export default TrustPaymentsDepositForm;
